/**
 * A tabela de preços de uma clínica.
 *
 * Mostra **todos** os exames do catálogo, não só os já negociados: a pergunta de quem abre esta
 * tela é o que ainda falta acertar com aquela clínica, e listar só o que já existe esconderia
 * exatamente isso.
 *
 * O preço calculado aparece ao lado do gravado. Quando divergem, a linha é destacada — é o sinal
 * de que a configuração mudou desde a última vez que os preços foram aplicados.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { PriceChange as PriceIcon } from '@mui/icons-material';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import pricingService from '../../services/pricingService';
import { providerService } from '../../services/providerService';
import { emReais, type ConfiguracaoDePreco, type ExamePrecificado } from '../../types/pricing';

interface ClinicaDaLista {
  id: number;
  name: string;
}

const colunas: DataTableColumn<ExamePrecificado>[] = [
  { id: 'examName', label: 'Exame', primary: true, render: (e) => e.examName },
  {
    id: 'baseCost',
    label: 'Referência',
    render: (e) => (e.baseCost === null ? '—' : emReais(e.baseCost)),
  },
  {
    id: 'custo',
    label: 'Custo negociado',
    render: (e) =>
      e.custo > 0 ? (
        emReais(e.custo)
      ) : (
        <Chip size="small" label="sem custo" variant="outlined" />
      ),
  },
  {
    id: 'preco',
    label: 'Preço calculado',
    render: (e) =>
      e.calculo === null ? (
        '—'
      ) : (
        <Box component="span" sx={{ fontWeight: 600 }}>{emReais(e.calculo.preco)}</Box>
      ),
  },
  {
    id: 'precoAtual',
    label: 'Preço gravado',
    render: (e) =>
      e.precoAtual === null ? (
        '—'
      ) : (
        <Box component="span" sx={{ color: e.divergente ? 'warning.main' : 'inherit' }}>
          {emReais(e.precoAtual)}
        </Box>
      ),
  },
  {
    id: 'lucro',
    label: 'Sobra',
    render: (e) => (e.calculo === null ? '—' : emReais(e.calculo.lucro)),
  },
];

export const ProviderPricingPage: React.FC = () => {
  const [clinicaId, setClinicaId] = useState('');
  const [itens, setItens] = useState<ExamePrecificado[]>([]);
  const [configuracao, setConfiguracao] = useState<ConfiguracaoDePreco | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregarClinicas = useCallback(
    async (): Promise<readonly ClinicaDaLista[]> => providerService.getProvidersForDropdown(),
    []
  );
  const clinicas = useListaPermitida<ClinicaDaLista>(
    'provider.read',
    'Clínicas',
    carregarClinicas
  );

  const carregarTabela = useCallback(async () => {
    if (!clinicaId) return;
    setCarregando(true);
    setErro(null);
    try {
      const tabela = await pricingService.tabelaDaClinica(clinicaId);
      setItens(tabela.itens);
      setConfiguracao(tabela.configuracao);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível carregar a tabela');
    } finally {
      setCarregando(false);
    }
  }, [clinicaId]);

  useEffect(() => {
    void carregarTabela();
  }, [carregarTabela]);

  async function aplicar(): Promise<void> {
    setAplicando(true);
    setErro(null);
    setAviso(null);
    try {
      const r = await pricingService.aplicarPrecos(clinicaId);
      setAviso(
        `${r.atualizados} preço(s) gravado(s).`
        + (r.semCusto > 0 ? ` ${r.semCusto} exame(s) ficaram de fora por não ter custo.` : '')
      );
      await carregarTabela();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível aplicar os preços');
    } finally {
      setAplicando(false);
    }
  }

  const divergentes = itens.filter((i) => i.divergente).length;
  const semCusto = itens.filter((i) => i.custo <= 0).length;

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Precificação', path: '/precificacao' },
        { label: 'Tabela por clínica' },
      ]}
      title="Tabela de preços por clínica"
      subtitle={
        configuracao
          ? `Imposto ${Number(configuracao.taxPercentage)}% · margem ${Number(configuracao.profitMarginPercentage)}%`
          : 'O preço sai do custo negociado com cada clínica'
      }
      actions={
        <Button
          variant="contained"
          startIcon={<PriceIcon />}
          onClick={() => void aplicar()}
          disabled={!clinicaId || aplicando || divergentes === 0}
        >
          Aplicar preços calculados
        </Button>
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}
      {aviso && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso(null)}>
          {aviso}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          select
          label="Clínica"
          value={clinicaId}
          onChange={(e) => setClinicaId(e.target.value)}
          disabled={!clinicas.permitido}
          helperText={clinicas.bloqueio ?? undefined}
          sx={{ minWidth: 280 }}
        >
          {clinicas.itens.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>

        {clinicaId && !carregando && (
          <Typography variant="body2" color="text.secondary">
            {divergentes > 0
              ? `${divergentes} preço(s) diferentes do calculado`
              : 'Todos os preços estão de acordo com o cálculo'}
            {semCusto > 0 ? ` · ${semCusto} exame(s) ainda sem custo negociado` : ''}
          </Typography>
        )}
      </Stack>

      <DataTable
        columns={colunas}
        rows={itens}
        getRowId={(e) => e.examId}
        loading={carregando}
        emptyState={{
          title: 'Escolha uma clínica para ver a tabela',
          description: `O catálogo tem os exames; o custo é o que você negociou com cada clínica.
            Escolhida a clínica, o preço de venda sai do custo pelo imposto e pela margem.`,
        }}
      />
    </PageShell>
  );
};

export default ProviderPricingPage;
